/**
 * AudioEngine - Moteur audio unifié pour VOYX
 * 
 * Singleton qui gère l'accès au microphone et l'analyse audio en temps réel.
 * Tous les modes (MiroirVocal, VoyxTime, Diagnostic) s'abonnent à ce moteur
 * au lieu d'avoir chacun leur propre getUserMedia.
 * 
 * Usage:
 *   import { audioEngine } from '../engine/AudioEngine'
 *   
 *   // S'abonner aux métriques
 *   const unsubscribe = audioEngine.subscribe((metrics) => {
 *     console.log(metrics.frequency, metrics.volume)
 *   })
 *   
 *   // Démarrer/arrêter
 *   await audioEngine.start()
 *   audioEngine.stop()
 */

import { requestMicrophone, defaultAudioConstraints, getFriendlyMicError } from '../utils/media';
import { MicVAD } from '@ricky0123/vad-web';

class AudioEngine {
    constructor() {
        // État
        this.isActive = false;
        this.error = null;

        // Métriques exposées
        this.metrics = {
            frequency: null,      // Hz - fréquence fondamentale détectée
            volume: 0,            // 0-1 - niveau RMS normalisé
            volumeDB: -100,       // dB - niveau en décibels
            speechRate: 0,        // 0-1 - estimation du débit de parole
            melody: 0,            // 0-1 - variation de pitch (mélodicité)
            resonance: 0,         // 0-1 - LTAS spectral slope (vocal projection)
            isActive: false       // true si on détecte une voix
        };

        // Références internes
        this._audioContext = null;
        this._analyser = null;
        this._mediaStream = null;
        this._analysisInterval = null;
        this._silenceTimeout = null;

        // Silero VAD — dual-mode matching iOS VoiceAnalysisService
        this._vad = null;
        this._vadActive = false;
        this._lastVadActiveTime = 0;       // timestamp of last Silero speech detection
        this._sileroModeTimeout = 2000;    // ms — match iOS sileroModeTimeout (2s)

        // Adaptive noise floor (match iOS bootstrapFrames concept)
        // Measures ambient noise in first ~3s, sets gate relative to that
        this._noiseFloorSamples = [];
        this._noiseFloorBootstrapped = false;
        this._noiseFloorDB = -60;
        this._noiseFloorBootstrapFrames = 20;  // ~3s at 150ms interval
        this._noiseFloorMargin = 7;            // dB above noise floor = gate threshold

        // Adaptive vocal range — learns the person's voice dynamics across sessions
        // vocalFloorDB = quietest voice detected (whisper), vocalCeilingDB = loudest (shout)
        // Volume maps within this range, NO clamp — truth above all
        const saved = this._loadVocalRange();
        this._vocalFloorDB = saved.floor;      // default -50 until learned
        this._vocalCeilingDB = saved.ceiling;   // default -20 until learned
        this._vocalRangeBootstrapped = saved.bootstrapped;

        // Subscribers
        this._subscribers = new Set();

        // Buffers pour calculs
        this._frequencyBuffer = [];
        this._volumeBuffer = [];
        this._lastPeakTime = 0;
        this._peakCount = 0;

        // Resonance LTAS buffers
        this._ltasBuffer = [];          // [{time, levels}] — harmonic levels per frame
        this._ltasTime = 0;             // monotonic clock (seconds)
        this._smoothedResonance = 0;

        // Configuration — match iOS VoiceAnalysisService
        this.config = {
            fftSize: 8192,               // match iOS Config.fftSize
            smoothingTimeConstant: 0.65,
            minDecibels: -100,
            maxDecibels: -10,
            analysisInterval: 150,       // ms — match iOS buffer delivery rate (~170ms for 8192/48kHz)
            silenceTimeout: 300,         // ms avant de déclarer silence (match iOS 0.3s)
            voiceThresholdDB: -53,       // match iOS hpsFallback: volumeDB > -53
            minFreq: 75,                 // Hz min pour détection voix (match iOS)
            maxFreq: 1000,               // Hz max pour détection voix
            bufferSize: 11,              // Taille du buffer de lissage fréquence (match iOS median 11)
            ltasWindowDuration: 1.5      // seconds — LTAS averaging window
        };
    }

    /**
     * Démarre l'analyse audio
     */
    async start() {
        if (this.isActive) {
            console.log('🎤 AudioEngine déjà actif');
            return true;
        }

        try {
            // Créer le contexte audio
            this._audioContext = new (window.AudioContext || window.webkitAudioContext)();

            // Créer l'analyseur
            this._analyser = this._audioContext.createAnalyser();
            this._analyser.fftSize = this.config.fftSize;
            this._analyser.smoothingTimeConstant = this.config.smoothingTimeConstant;
            this._analyser.minDecibels = this.config.minDecibels;
            this._analyser.maxDecibels = this.config.maxDecibels;

            // Obtenir le flux micro — NO AGC (match iOS .measurement mode)
            // AGC defeats volume measurement and raises noise floor
            this._mediaStream = await requestMicrophone({
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
                sampleRate: 48000,
                channelCount: 1
            });

            // Connecter le micro à l'analyseur
            const source = this._audioContext.createMediaStreamSource(this._mediaStream);
            source.connect(this._analyser);

            // Démarrer la boucle d'analyse
            this._analysisInterval = setInterval(() => this._analyze(), this.config.analysisInterval);

            // Start Silero VAD — gates frequency buffer (match iOS VoiceAnalysisService)
            // Assets served from CDN (Vite doesn't serve node_modules WASM/ONNX files)
            try {
                this._vad = await MicVAD.new({
                    stream: this._mediaStream,
                    baseAssetPath: "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.30/dist/",
                    onnxWASMBasePath: "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.24.3/dist/",
                    onSpeechStart: () => {
                        this._vadActive = true;
                    },
                    onSpeechEnd: () => {
                        this._vadActive = false;
                    },
                    onVADMisfire: () => {
                        // Misfire = Silero thought it was speech but wasn't
                        // Reset lastVadActiveTime so inSileroMode deactivates immediately
                        // → fallback resumes, humming continues to animate
                        this._vadActive = false;
                        this._lastVadActiveTime = 0;
                    }
                });
                this._vad.start();
                console.log('✅ Silero VAD démarré');
            } catch (vadErr) {
                console.warn('⚠️ Silero VAD indisponible, fallback volume gate:', vadErr.message);
                // Fallback: _vadActive stays false, volume gate in _analyze() still works
            }

            this.isActive = true;
            this.error = null;
            console.log('✅ AudioEngine démarré');

            return true;
        } catch (err) {
            this.error = getFriendlyMicError(err);
            console.error('❌ AudioEngine erreur:', this.error);
            this.stop();
            return false;
        }
    }

    /**
     * Arrête l'analyse audio
     */
    stop() {
        console.log('🛑 AudioEngine arrêt');

        // Arrêter l'analyse
        if (this._analysisInterval) {
            clearInterval(this._analysisInterval);
            this._analysisInterval = null;
        }

        // Arrêter le timeout silence
        if (this._silenceTimeout) {
            clearTimeout(this._silenceTimeout);
            this._silenceTimeout = null;
        }

        // Arrêter Silero VAD
        if (this._vad) {
            this._vad.pause();
            this._vad.destroy();
            this._vad = null;
            this._vadActive = false;
        }

        // Arrêter le stream
        if (this._mediaStream) {
            this._mediaStream.getTracks().forEach(track => track.stop());
            this._mediaStream = null;
        }

        // Fermer le contexte audio
        if (this._audioContext && this._audioContext.state !== 'closed') {
            this._audioContext.close();
            this._audioContext = null;
        }

        // Réinitialiser les buffers
        this._frequencyBuffer = [];
        this._volumeBuffer = [];
        this._peakCount = 0;
        this._ltasBuffer = [];
        this._ltasTime = 0;
        this._smoothedResonance = 0;
        this._noiseFloorSamples = [];
        this._noiseFloorBootstrapped = false;
        this._noiseFloorDB = -60;
        this.config.voiceThresholdDB = -53; // reset to default, re-bootstraps on next start

        // Réinitialiser les métriques
        this.metrics = {
            frequency: null,
            volume: 0,
            volumeDB: -100,
            speechRate: 0,
            melody: 0,
            resonance: 0,
            isActive: false
        };

        this.isActive = false;

        // Notifier les subscribers
        this._notify();
    }

    /**
     * S'abonner aux métriques audio
     * @param {Function} callback - Fonction appelée avec les métriques
     * @returns {Function} Fonction pour se désabonner
     */
    subscribe(callback) {
        this._subscribers.add(callback);

        // Retourner la fonction de désabonnement
        return () => {
            this._subscribers.delete(callback);
        };
    }

    /**
     * Notifier tous les subscribers
     */
    _notify() {
        const metrics = { ...this.metrics };
        this._subscribers.forEach(callback => {
            try {
                callback(metrics);
            } catch (err) {
                console.error('AudioEngine subscriber error:', err);
            }
        });
    }

    /**
     * Boucle d'analyse principale — ported from iOS VoiceAnalysisService
     */
    _analyze() {
        if (!this._analyser || !this._mediaStream) return;

        const bufferLength = this._analyser.frequencyBinCount;
        const freqData = new Float32Array(bufferLength);
        const timeData = new Float32Array(bufferLength);

        this._analyser.getFloatFrequencyData(freqData);   // dB values
        this._analyser.getFloatTimeDomainData(timeData);

        const sampleRate = this._audioContext.sampleRate;
        const fftSize = this._analyser.fftSize;
        const frequencyResolution = sampleRate / fftSize;
        const halfN = bufferLength; // frequencyBinCount = fftSize/2

        // 1. VOLUME (RMS) — Adaptive vocal range
        // Maps dB within the person's learned vocal range (persisted across sessions).
        // No ceiling clamp — if they exceed their loudest, torus shows it truthfully.
        let rms = 0;
        for (let i = 0; i < timeData.length; i++) {
            rms += timeData[i] * timeData[i];
        }
        rms = Math.sqrt(rms / timeData.length);
        const volumeDB = 20 * Math.log10(Math.max(rms, 0.0001));
        const vocalRange = Math.max(1, this._vocalCeilingDB - this._vocalFloorDB); // avoid /0
        const volume = Math.max(0, (volumeDB - this._vocalFloorDB) / vocalRange);

        // 2. HPS FREQUENCY DETECTION (match iOS VoiceAnalysisService)
        // Convert dB spectrum to linear magnitudes (AnalyserNode gives dB, HPS multiplies)
        const linearMags = new Float32Array(halfN);
        for (let i = 0; i < halfN; i++) {
            linearMags[i] = Math.pow(10, freqData[i] / 20);
        }

        const minBin = Math.floor(this.config.minFreq / frequencyResolution);
        const maxBin = Math.ceil(this.config.maxFreq / frequencyResolution);
        const hpsBinCount = maxBin - minBin;

        let frequency = null;

        if (hpsBinCount > 0) {
            // Initialize HPS buffer with magnitudes in voice range
            const hpsBuffer = new Float32Array(hpsBinCount);
            for (let i = 0; i < hpsBinCount; i++) {
                hpsBuffer[i] = linearMags[minBin + i];
            }

            // Multiply by downsampled harmonics x2 through x5
            for (let harmonic = 2; harmonic <= 5; harmonic++) {
                for (let i = 0; i < hpsBinCount; i++) {
                    const sourceBin = (minBin + i) * harmonic;
                    if (sourceBin < halfN) {
                        hpsBuffer[i] *= linearMags[sourceBin];
                    } else {
                        hpsBuffer[i] = 0;
                    }
                }
            }

            // Find peak in HPS product
            let maxVal = 0;
            let maxIdx = 0;
            for (let i = 0; i < hpsBinCount; i++) {
                if (hpsBuffer[i] > maxVal) {
                    maxVal = hpsBuffer[i];
                    maxIdx = i;
                }
            }

            if (maxVal > 0) {
                const peakBin = minBin + maxIdx;

                // Parabolic interpolation on HPS buffer
                if (maxIdx > 0 && maxIdx < hpsBinCount - 1) {
                    const alpha = hpsBuffer[maxIdx - 1];
                    const beta = hpsBuffer[maxIdx];
                    const gamma = hpsBuffer[maxIdx + 1];
                    const denominator = alpha - 2 * beta + gamma;

                    if (Math.abs(denominator) > 0.0001) {
                        const p = Math.max(-0.5, Math.min(0.5, 0.5 * (alpha - gamma) / denominator));
                        frequency = (peakBin + p) * frequencyResolution;
                    } else {
                        frequency = peakBin * frequencyResolution;
                    }
                } else {
                    frequency = peakBin * frequencyResolution;
                }
            }
        }

        // 2b. ADAPTIVE NOISE FLOOR — bootstrap from first ~3s of ambient noise
        // Collects dB samples before any speech, uses 25th percentile as floor
        // Gate threshold = noiseFloor + margin (adapts to any mic/room)
        if (!this._noiseFloorBootstrapped) {
            // Only collect samples when Silero says no speech (or VAD not loaded yet)
            if (!this._vadActive) {
                this._noiseFloorSamples.push(volumeDB);
            }
            if (this._noiseFloorSamples.length >= this._noiseFloorBootstrapFrames) {
                const sorted = [...this._noiseFloorSamples].sort((a, b) => a - b);
                this._noiseFloorDB = sorted[Math.floor(sorted.length / 4)]; // 25th percentile
                this.config.voiceThresholdDB = this._noiseFloorDB + this._noiseFloorMargin;
                this._noiseFloorBootstrapped = true;
                console.log(`🔇 Noise floor: ${this._noiseFloorDB.toFixed(1)}dB → gate: ${this.config.voiceThresholdDB.toFixed(1)}dB`);
            }
        }

        // 3. VOICE GATE — dual-mode matching iOS VoiceAnalysisService lines 278-289
        // Mode Silero (spoke within last 2s): Silero controls silences, fallback disabled.
        //   → Clean pauses between words, background noise ignored.
        // Mode fallback (no speech for 2s+): any loud sound with frequency activates.
        //   → Humming, tapping, ambient sounds animate the torus.
        const now = Date.now();
        const sileroActive = this._vad ? this._vadActive : false;
        if (sileroActive) {
            this._lastVadActiveTime = now;
        }
        // When Silero is loaded, trust it fully — no HPS fallback bleeding through.
        // HPS fallback only activates if Silero failed to load entirely.
        const hpsFallback = !this._vad && frequency !== null && volumeDB >= this.config.voiceThresholdDB;
        const voiceDetected = sileroActive || hpsFallback;

        // Learn vocal range from every frame where voice is detected
        if (voiceDetected && volumeDB > -80) {
            this._updateVocalRange(volumeDB);
        }

        if (frequency === null || !voiceDetected) {
            if (!this._silenceTimeout) {
                this._silenceTimeout = setTimeout(() => {
                    this.metrics.frequency = null;
                    this.metrics.isActive = false;
                    this.metrics.resonance = 0;
                    this._frequencyBuffer = [];
                    this._volumeBuffer = [];
                    this._smoothedResonance = 0;
                    this._notify();
                    this._silenceTimeout = null;
                }, this.config.silenceTimeout);
            }
            return;
        }

        // Cancel silence timeout
        if (this._silenceTimeout) {
            clearTimeout(this._silenceTimeout);
            this._silenceTimeout = null;
        }

        // 4. MEDIAN FILTER (match iOS — eliminates HPS outliers)
        this._frequencyBuffer.push(frequency);
        if (this._frequencyBuffer.length > this.config.bufferSize) {
            this._frequencyBuffer.shift();
        }

        const sorted = [...this._frequencyBuffer].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        const medianFreq = sorted.length % 2 === 0
            ? (sorted[mid - 1] + sorted[mid]) / 2
            : sorted[mid];

        this._volumeBuffer.push(volume);
        if (this._volumeBuffer.length > this.config.bufferSize) {
            this._volumeBuffer.shift();
        }

        // 5. MELODICITY (pitch variation)
        let melody = 0;
        if (this._frequencyBuffer.length > 5) {
            const avg = this._frequencyBuffer.reduce((a, b) => a + b, 0) / this._frequencyBuffer.length;
            const variance = this._frequencyBuffer.reduce((sum, f) => sum + Math.pow(f - avg, 2), 0) / this._frequencyBuffer.length;
            melody = Math.min(1, Math.sqrt(variance) / 50);
        }

        // 6. SPEECH RATE (volume peak counting)
        if (volume > 0.3 && now - this._lastPeakTime > 100) {
            this._peakCount++;
            this._lastPeakTime = now;
        }
        const speechRate = Math.min(1, this._peakCount / 8);
        if (now - this._lastPeakTime > 1000) {
            this._peakCount = 0;
        }

        // 7. RESONANCE — LTAS spectral slope (ported from iOS)
        this._ltasTime += this.config.analysisInterval / 1000;

        const harmonicLevels = this._extractHarmonicLevels(medianFreq, linearMags, sampleRate);
        if (harmonicLevels) {
            this._ltasBuffer.push({ time: this._ltasTime, levels: harmonicLevels });
        }

        // Evict old LTAS entries outside window
        const windowStart = this._ltasTime - this.config.ltasWindowDuration;
        while (this._ltasBuffer.length > 0 && this._ltasBuffer[0].time < windowStart) {
            this._ltasBuffer.shift();
        }

        // Compute LTAS slope → resonance score
        const ltasScore = this._computeLTASSlope();
        // EMA: rise fast (0.3) when data, decay slowly (×0.95) when insufficient
        if (this._ltasBuffer.length >= 5) {
            this._smoothedResonance += 0.3 * (ltasScore - this._smoothedResonance);
        } else {
            this._smoothedResonance *= 0.95;
        }

        // 8. UPDATE METRICS
        this.metrics = {
            frequency: Math.round(medianFreq),
            volume: volume,
            volumeDB: volumeDB,
            speechRate: speechRate,
            melody: melody,
            resonance: this._smoothedResonance,
            isActive: true
        };

        // DEBUG: log metrics every ~1s (every 7th call at 150ms interval)
        if (!this._debugCounter) this._debugCounter = 0;
        if (++this._debugCounter % 7 === 0) {
            const range = `${this._vocalFloorDB.toFixed(0)}→${this._vocalCeilingDB.toFixed(0)}dB`;
            console.log(`🎤 vol=${(volume*100).toFixed(0)}% (${volumeDB.toFixed(1)}dB) [${range}] | freq=${Math.round(medianFreq)}Hz | res=${(this._smoothedResonance*100).toFixed(0)}%`);
        }

        this._notify();
    }

    /**
     * Extract noise-corrected harmonic levels for current frame.
     * Returns {1: 0, 2: -3.5, ...} (dB relative to H1) or null.
     * Ported from iOS VoiceAnalysisService.extractHarmonicLevels()
     */
    _extractHarmonicLevels(f0, linearMags, sampleRate) {
        const freqRes = sampleRate / this._analyser.fftSize;
        const halfN = linearMags.length;
        const f0Bin = f0 / freqRes;

        // Harmonics up to 1500 Hz or 5 max
        const maxHarmonic = Math.min(Math.floor(1500 / f0), 5);
        if (maxHarmonic < 3 || f0Bin < 2) return null;

        // Noise floor: 25th percentile of magnitudes in voice range
        const nfMinBin = Math.max(1, Math.floor(60 / freqRes));
        const nfMaxBin = Math.min(halfN - 1, Math.floor(1500 / freqRes));
        const nfMags = [];
        for (let i = nfMinBin; i <= nfMaxBin; i++) {
            nfMags.push(linearMags[i]);
        }
        nfMags.sort((a, b) => a - b);
        const noiseFloor = nfMags.length > 0 ? nfMags[Math.floor(nfMags.length / 4)] : 0;
        const noiseFloorSq = noiseFloor * noiseFloor;

        const points = [];
        for (let k = 1; k <= maxHarmonic; k++) {
            const centerBin = Math.round(k * f0Bin);
            if (centerBin <= 1 || centerBin >= halfN - 1) continue;

            const peakMag = Math.max(
                linearMags[centerBin - 1],
                linearMags[centerBin],
                linearMags[centerBin + 1]
            );

            // Must be at least 3dB above noise floor
            if (peakMag <= noiseFloor * 1.41) break;

            // Noise subtraction
            const trueMag = Math.sqrt(Math.max(0, peakMag * peakMag - noiseFloorSq));
            if (trueMag <= 0) break;

            const db = 20 * Math.log10(trueMag);

            // Drop >25dB from H1 = unreliable
            if (points.length > 0 && db < points[0].db - 25) break;

            points.push({ k, db });
        }

        if (points.length < 3) return null;

        // Normalize to H1 = 0 dB
        const h1dB = points[0].db;
        const levels = {};
        for (const p of points) {
            levels[p.k] = p.db - h1dB;
        }

        return levels;
    }

    /**
     * Compute spectral slope via linear regression from averaged harmonic levels.
     * Returns score 0-1 (0 = steep rolloff/breathy, 1 = flat/max resonance).
     * Ported from iOS VoiceAnalysisService.computeLTASSlope()
     */
    _computeLTASSlope() {
        if (this._ltasBuffer.length < 5) return 0;

        // Average each harmonic position across the buffer
        const sums = {};
        const counts = {};
        for (const entry of this._ltasBuffer) {
            for (const [k, level] of Object.entries(entry.levels)) {
                sums[k] = (sums[k] || 0) + level;
                counts[k] = (counts[k] || 0) + 1;
            }
        }

        // Build averaged points (only harmonics present in >50% of frames)
        const minCount = Math.floor(this._ltasBuffer.length / 2);
        const avgPoints = [];
        for (let k = 1; k <= 5; k++) {
            if (sums[k] !== undefined && counts[k] >= minCount) {
                avgPoints.push({ x: k, y: sums[k] / counts[k] });
            }
        }

        if (avgPoints.length < 3) return 0;

        // Linear regression on LTAS harmonics
        const n = avgPoints.length;
        let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
        for (const p of avgPoints) {
            sumX += p.x; sumY += p.y; sumXY += p.x * p.y; sumX2 += p.x * p.x;
        }

        const denom = n * sumX2 - sumX * sumX;
        if (Math.abs(denom) <= 0.001) return 0;

        const slope = (n * sumXY - sumX * sumY) / denom;

        // Map [-5, 0] → [0, 1]
        return Math.max(0, Math.min(1, (slope + 5) / 5));
    }

    /**
     * Obtenir le flux audio brut (pour l'enregistrement)
     */
    getStream() {
        return this._mediaStream;
    }

    /**
     * Obtenir l'analyseur (pour usage avancé)
     */
    getAnalyser() {
        return this._analyser;
    }

    // --- Adaptive Vocal Range (persisted across sessions) ---

    _loadVocalRange() {
        try {
            const raw = localStorage.getItem('voyx_vocal_range');
            if (raw) {
                const data = JSON.parse(raw);
                console.log(`🎙️ Vocal range loaded: floor=${data.floor?.toFixed(1)}dB ceiling=${data.ceiling?.toFixed(1)}dB`);
                return { floor: data.floor, ceiling: data.ceiling, bootstrapped: true };
            }
        } catch (e) { /* ignore corrupt data */ }
        return { floor: -50, ceiling: -20, bootstrapped: false };
    }

    _saveVocalRange() {
        try {
            localStorage.setItem('voyx_vocal_range', JSON.stringify({
                floor: this._vocalFloorDB,
                ceiling: this._vocalCeilingDB
            }));
        } catch (e) { /* quota exceeded, ignore */ }
    }

    _updateVocalRange(db) {
        let changed = false;
        if (db < this._vocalFloorDB) {
            this._vocalFloorDB = db;
            changed = true;
        }
        if (db > this._vocalCeilingDB) {
            this._vocalCeilingDB = db;
            changed = true;
        }
        if (!this._vocalRangeBootstrapped) {
            this._vocalRangeBootstrapped = true;
            changed = true;
        }
        if (changed) {
            this._saveVocalRange();
            console.log(`🎙️ Vocal range updated: floor=${this._vocalFloorDB.toFixed(1)}dB ceiling=${this._vocalCeilingDB.toFixed(1)}dB`);
        }
    }
}

// Singleton
export const audioEngine = new AudioEngine();

// Export par défaut aussi
export default audioEngine;
